import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { IconDefinition, faClipboard, faFileAlt, faPaperclip, faSearch, faUser, faUsers } from '@fortawesome/pro-duotone-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import _, { concat } from 'lodash';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, switchMap, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { formatBytes } from 'src/app/helper/byteFormattingHelper';
import { MessageActions } from 'src/app/samwebDescription/samweb/messages/MessageActions';
import { LongRunningContextCategoryTyp } from 'src/app/samwebDescription/samweb/waitingContext/waitingContextCategory';
import { EmailPurpose } from 'src/app/samwebDescription/samwin/EmailPurpose';
import { SelectedSubscriberSelectors } from 'src/app/store/samwin/directory/selected-subscriber/selected-subscriber.selectors';
import { Email } from 'src/app/store/samwin/email/email-behaviour/email';
import { EmailContactFieldValueUpdated, EmailFieldValueUpdated, UpdateValidation } from 'src/app/store/samwin/email/email-behaviour/email-behaviour.actions';
import { EmailBehaviourStateSelectors } from 'src/app/store/samwin/email/email-behaviour/email-behaviour.selectors';
import { EmailBehaviourState } from 'src/app/store/samwin/email/email-behaviour/email-behaviour.state';
import { EmailContact } from 'src/app/store/samwin/email/email-behaviour/email-contact';
import {
   ChangeEditorToReader,
   EmailTemplateSelected,
   EndEmailProcess,
   QueryDirectoryEmails,
   SendEmailProcess,
   UpdateDirectoryEmails,
} from 'src/app/store/samwin/email/email-dialog/email-dialog-behaviour.actions';
import { EmailDialogSelectors } from 'src/app/store/samwin/email/email-dialog/email-dialog-behaviour.selectors';
import { SamwinAgentListState } from 'src/app/store/samwin/samwin-agent-list/samwin-agent-list.state';
import { MessageInfo } from 'src/app/store/samwin/samwin-mail-list/message-info';
import { SamwinMessageListStateSelectors } from 'src/app/store/samwin/samwin-mail-list/samwin-message-list.selectors';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { SkillGroup } from 'src/app/store/samwin/samwin-skills/skill-group';
import { LongRunningContextStopped } from 'src/app/store/server-communication/running-actions/running-actions.actions';
import { SessionState } from 'src/app/store/session/session.state';
import { AgentListEntryRemoteDto } from 'src/generated/signalR/agent-list-entry-remote-dto';
import { EmailTemplateShortInfoRemoteDto } from 'src/generated/signalR/email-template-short-info-remote-dto';
import { SamwinCallInfoRemoteDto } from 'src/generated/signalR/samwin-call-info-remote-dto';
import { AttachmentRemoteDto, EmailTemplateRemoteDto } from 'src/generated/swagger';

@Component({
   selector: 'samweb-email-editor-window',
   templateUrl: './email-editor-window.component.html',
   styleUrls: ['./email-editor-window.component.scss'],
})
export class EmailEditorWindowComponent implements OnInit, OnDestroy {
   private destroy$ = new Subject();
   public windowState = 'default';

   public noDataIcon: IconDefinition = faSearch;
   public fileIcon: IconDefinition = faFileAlt;
   public userIcon: IconDefinition = faUser;
   public usersIcon: IconDefinition = faUsers;

   public clipBoardIcon: IconDefinition = faClipboard;

   public uploadIcon: IconDefinition = faPaperclip;

   @Input()
   public messageId: string;

   @Input()
   public email$: Observable<Email>;

   @Input()
   public hasReferenceMail$: Observable<boolean>;

   @Output()
   public uploadCanceled = new EventEmitter();

   public purpose$: Observable<EmailPurpose>;

   public isEmailingInProgress$: Observable<boolean>;

   public isTemplateSelected$: Observable<boolean>;

   public isLoadingTemplates$: Observable<boolean>;

   public contextData$: Observable<any>;

   public selectedTemplate$: Observable<EmailTemplateRemoteDto>;

   public filteredSelectableTemplates$: Observable<EmailTemplateShortInfoRemoteDto[]>;

   @Select(SamwinSkillsState.callSkillsInclusiveOverallSkill)
   public skillList$: Observable<SkillGroup[]>;

   public callInfo$: Observable<SamwinCallInfoRemoteDto>;

   public currentUser$: Observable<AgentListEntryRemoteDto>;

   public isMessageSizeLimitReached$: Observable<boolean>;

   public messageMaximumSizeFormatted: string;

   public isCcShown: boolean;

   public isBccShown: boolean;

   public isAttachmentShown: boolean;

   public attachments$: Observable<AttachmentRemoteDto[]>;

   public isValidated$: Observable<boolean>;

   public inValidMessage$: Observable<string>;

   public isFromInvalid$: Observable<boolean>;

   public isToInvalid$: Observable<boolean>;

   public isSubjectInvalid$: Observable<boolean>;

   public isContentInvalid$: Observable<boolean>;

   public isValidationEnabled$: Observable<boolean>;

   public isCcEmpty$: Observable<boolean>;

   public isBccEmpty$: Observable<boolean>;

   public isEmailConnectorNone$: Observable<boolean>;

   public isValidated: boolean;

   public emailReceiver$: Observable<string>;

   public emailContent$: Observable<string>;

   @Select(SamwinAgentListState.getAgentListEmails)
   public agentInfoList$: Observable<EmailContact[]>;

   public directoryInfoList$: Observable<EmailContact[]>;

   public selectedSubscriberEmailOptions$: Observable<EmailContact[]>;

   public combinedList$: Observable<EmailContact[]>;

   public autocompleteMatches$: Observable<EmailContact[]>;

   public searchTerms$ = new BehaviorSubject<string>('');

   public recipientTo$: Observable<EmailContact[]>;
   public recipientCc$: Observable<EmailContact[]>;
   public recipientBcc$: Observable<EmailContact[]>;

   public messageInfo$: Observable<MessageInfo>;

   constructor(private store: Store, private translateService: TranslateService) {}

   ngOnInit() {
      this.messageInfo$ = this.store.select(SamwinMessageListStateSelectors.getMessageById).pipe(map((getMessage) => getMessage(this.messageId)));

      this.recipientTo$ = this.email$.pipe(map((email) => email?.to ?? []));
      this.recipientCc$ = this.email$.pipe(map((email) => email?.cc ?? []));
      this.recipientBcc$ = this.email$.pipe(map((email) => email?.bcc ?? []));

      this.directoryInfoList$ = this.store
         .select(EmailBehaviourState.getDirectoryMailAddress)
         .pipe(map((getDirectoryMails) => getDirectoryMails(this.messageId)));

      this.selectedSubscriberEmailOptions$ = this.store.select(SelectedSubscriberSelectors.selectedSubscriberEmailOptions);

      this.combinedList$ = combineLatest([this.agentInfoList$, this.directoryInfoList$, this.selectedSubscriberEmailOptions$]).pipe(
         map(([agentInfoList, directoryInfoList, selectedSubscriberEmailOptions]) => {
            const combinedAndUniqueList = _(concat(agentInfoList, directoryInfoList))
               .uniqBy((x) => x.displayText)
               .sortBy((x) => x.displayText)
               .value();
            return _(concat(selectedSubscriberEmailOptions, combinedAndUniqueList)).value();
         })
      );

      const anySelectedEntries$ = combineLatest([this.recipientTo$, this.recipientCc$, this.recipientBcc$]).pipe(map(([to, cc, bcc]) => _.concat(to, cc, bcc)));
      this.autocompleteMatches$ = combineLatest([this.combinedList$, this.searchTerms$, anySelectedEntries$])
         .pipe(
            map(([entries, searchTerms, selectedEntries]) => {
               return entries.filter((entry) => {
                  const entryParts = entry.displayText.split(' ').map((str) => str.trim());
                  return (
                     searchTerms
                        .toLowerCase()
                        .split(' ')
                        .every((term) => {
                           return entryParts.some((part) => part.toLowerCase().includes(term));
                        }) && !_(selectedEntries)?.some((selectedEntry) => selectedEntry.displayText?.includes(entry.displayText))
                  );
               });
            })
         )
         .pipe(map((entries) => entries ?? []));

      this.emailContent$ = this.email$.pipe(map((email) => email?.body));

      this.purpose$ = this.store.select(EmailDialogSelectors.getPurpose).pipe(map((getPurpose) => getPurpose(this.messageId)));

      this.isEmailingInProgress$ = this.store
         .select(EmailDialogSelectors.isEmailingInProgress)
         .pipe(map((isEmailingInProgress) => isEmailingInProgress(this.messageId)));

      this.isTemplateSelected$ = this.store.select(EmailDialogSelectors.isTemplateSelected).pipe(map((isTemplateSeleced) => isTemplateSeleced(this.messageId)));

      this.isLoadingTemplates$ = this.store
         .select(EmailDialogSelectors.isLoadingTemplates)
         .pipe(map((isLoadingTemplates) => isLoadingTemplates(this.messageId)));

      this.contextData$ = this.store
         .select(EmailDialogSelectors.getTemplateContextData)
         .pipe(map((getTemplateContextData) => getTemplateContextData(this.messageId)));

      this.selectedTemplate$ = this.store.select(EmailDialogSelectors.getSelectedTemplate).pipe(map((getTemplate) => getTemplate(this.messageId)));

      this.isEmailConnectorNone$ = this.store.select(EmailDialogSelectors.getRequestingSkill).pipe(
         map((getSkill) => getSkill(this.messageId)),
         switchMap((skillId) => this.store.select(SamwinSkillsState.hasCurrentUserEmailConnector).pipe(map((hasConnector) => !hasConnector(skillId))))
      );

      this.filteredSelectableTemplates$ = this.store
         .select(EmailDialogSelectors.getFilteredTemplates)
         .pipe(map((getTemplates) => getTemplates(this.messageId)));

      this.callInfo$ = this.contextData$.pipe(
         map((x) => {
            if (_.has(x, 'info')) {
               return x;
            }
            return null;
         })
      );

      const messageMaximumSize = this.store.selectSnapshot(SessionState.getAuthenticatedUser).emailMaximumSize;

      this.attachments$ = this.store.select(EmailBehaviourState.getAttachments).pipe(map((getAttachments) => getAttachments(this.messageId)));

      this.isValidationEnabled$ = this.store
         .select(EmailBehaviourState.isValidationEnabled)
         .pipe(map((isValidationEnabled) => isValidationEnabled(this.messageId)));

      this.isMessageSizeLimitReached$ = this.store.select(EmailBehaviourState.getEmailSize).pipe(
         map((getEmailSize) => getEmailSize(this.messageId)),
         map((size) => size > messageMaximumSize)
      );

      this.isFromInvalid$ = this.email$.pipe(map((email) => email?.from.length === 0));
      this.isToInvalid$ = this.recipientTo$.pipe(map((recipient) => recipient?.length === 0));
      this.isSubjectInvalid$ = this.email$.pipe(map((email) => email?.subject.length === 0));
      this.isContentInvalid$ = this.email$.pipe(map((email) => email?.body.length === 0));

      this.isCcEmpty$ = this.email$.pipe(map((email) => email?.cc.length === 0));
      this.isBccEmpty$ = this.email$.pipe(map((email) => email?.bcc.length === 0));

      this.isValidated$ = combineLatest([
         this.isFromInvalid$,
         this.isToInvalid$,
         this.isSubjectInvalid$,
         this.isContentInvalid$,
         this.isMessageSizeLimitReached$,
         this.isEmailConnectorNone$,
      ]).pipe(
         map(
            ([isFromInvalid, isToInvalid, isSubjectInvalid, isContentInvalid, isMessageSizeLimitReached, isEmailConnectorNone]) =>
               !(isFromInvalid || isToInvalid || isSubjectInvalid || isContentInvalid || isMessageSizeLimitReached || isEmailConnectorNone)
         )
      );

      this.inValidMessage$ = combineLatest([
         this.isValidationEnabled$,
         this.isFromInvalid$,
         this.isToInvalid$,
         this.isSubjectInvalid$,
         this.isContentInvalid$,
         this.isMessageSizeLimitReached$,
         this.isEmailConnectorNone$,
      ]).pipe(
         map(([isValidationEnabled, isFromInvalid, isToInvalid, isSubjectInvalid, isContentInvalid, isSizeLimitReached, isEmailConnectorNone]) => {
            let message = '';
            if (isFromInvalid && isValidationEnabled) {
               message += this.translateService.instant('samwin.email_dialog.message_requires_sender');
               message += '<br/>';
            }
            if (isToInvalid && isValidationEnabled) {
               message += this.translateService.instant('samwin.email_dialog.message_requires_recipient');
               message += '<br/>';
            }
            if (isSubjectInvalid && isValidationEnabled) {
               message += this.translateService.instant('samwin.email_dialog.message_requires_subject');
               message += '<br/>';
            }
            if (isContentInvalid && isValidationEnabled) {
               message += this.translateService.instant('samwin.email_dialog.message_requires_content');
               message += '<br/>';
            }
            if (isSizeLimitReached) {
               message += this.translateService.instant('samwin.email_dialog.message_over_size_limit', {
                  message_size: this.store.selectSnapshot(EmailBehaviourStateSelectors.getEmailSizeFormatted)(this.messageId),
                  max_size: formatBytes(messageMaximumSize, 2, this.translateService.currentLang),
               });
               message += '<br/>';
            }
            if (isEmailConnectorNone) {
               message += this.translateService.instant('samwin.email_dialog.message_requires_connector');
            }
            return message;
         })
      );

      this.isValidated$.pipe(takeUntil(this.destroy$)).subscribe((isValid) => (this.isValidated = isValid));

      // NOTE: check on startup if fields are filled.
      this.isCcEmpty$
         .pipe(
            tap((isEmpty) => {
               if (!isEmpty) {
                  this.isCcShown = !isEmpty;
               }
            }),
            takeWhile((isEmpty) => isEmpty),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isBccEmpty$
         .pipe(
            tap((isEmpty) => {
               if (!isEmpty) {
                  this.isBccShown = !isEmpty;
               }
            }),
            takeWhile((isEmpty) => isEmpty),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.attachments$
         .pipe(
            tap((attachments) => {
               if (attachments?.length > 0) {
                  this.isAttachmentShown = attachments.length > 0;
               }
            }),
            takeWhile((attachments) => !(attachments?.length > 0)),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.searchTerms$
         .pipe(
            filter((searchTerms) => !_.isEmpty(searchTerms.trim())),
            takeUntil(this.destroy$)
         )
         .subscribe((searchTerms) => this.store.dispatch(new QueryDirectoryEmails(this.messageId, searchTerms, false, { take: 50 })));

      this.searchTerms$
         .pipe(
            filter((searchTerms) => _.isEmpty(searchTerms.trim())),
            takeUntil(this.destroy$)
         )
         .subscribe(() => this.store.dispatch(new UpdateDirectoryEmails(this.messageId, [])));
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   selectTemplate(template: EmailTemplateShortInfoRemoteDto) {
      this.store.dispatch(new EmailTemplateSelected(template, this.messageId));
   }

   onSend(): void {
      if (!this.isValidated) {
         this.store.dispatch(new UpdateValidation(this.messageId, true));
         return;
      }
      this.store
         .dispatch(new SendEmailProcess(this.messageId))
         .pipe(takeUntil(this.destroy$))
         .subscribe(() => {
            this.store.dispatch(new LongRunningContextStopped(LongRunningContextCategoryTyp.EMAIL_WINDOW, this.messageId));
            this.store.dispatch(new EndEmailProcess(this.messageId));
         });
   }

   onSendAndComplete(messageInfo: MessageInfo): void {
      if (!this.isValidated) {
         this.store.dispatch(new UpdateValidation(this.messageId, true));
         return;
      }
      this.store
         .dispatch(new SendEmailProcess(this.messageId))
         .pipe(takeUntil(this.destroy$))
         .subscribe(() => {
            this.store.dispatch(new LongRunningContextStopped(LongRunningContextCategoryTyp.EMAIL_WINDOW, this.messageId));
            MessageActions.COMPLETE.executeAction$(this.store, messageInfo);
         });
   }

   onSendAndTrack(messageInfo: MessageInfo): void {
      if (!this.isValidated) {
         this.store.dispatch(new UpdateValidation(this.messageId, true));
         return;
      }
      this.store
         .dispatch(new SendEmailProcess(this.messageId))
         .pipe(takeUntil(this.destroy$))
         .subscribe(() => {
            this.store.dispatch(new LongRunningContextStopped(LongRunningContextCategoryTyp.EMAIL_WINDOW, this.messageId));
            MessageActions.TRACK.executeAction$(this.store, messageInfo);
         });
   }

   onCancel(): void {
      this.store.dispatch(new ChangeEditorToReader(this.messageId));
   }

   onToggleCc(): void {
      this.isCcShown = !this.isCcShown;
   }

   onToggleBcc(): void {
      this.isBccShown = !this.isBccShown;
   }

   onToggleAttachments(): void {
      this.isAttachmentShown = !this.isAttachmentShown;
   }

   textChanged(value: string, field: 'from' | 'subject' | 'body') {
      this.store.dispatch(new EmailFieldValueUpdated(value, this.messageId, field));
   }

   updateMultiselectSearchValue(value: string): void {
      this.searchTerms$.next(value);
   }

   onEntrySelected(selectedEntries: EmailContact[], field: 'to' | 'cc' | 'bcc') {
      this.store.dispatch(new EmailContactFieldValueUpdated(selectedEntries ?? [], this.messageId, field));
   }

   attachmentUploadCanceled(value: boolean) {
      this.uploadCanceled.emit(value);
   }
}
